import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useMutation } from '@apollo/client';
import styles from './InspectionItemText.module.scss';
import { Text } from '../../typography';
import { Icon, Tooltip } from '@mui/material';
import { loader } from 'graphql.macro';
import { Table } from 'react-bootstrap';
import SimpleUserCard from '../../SimpleUserCard';
import { calculateRisk, colorLookup } from '../../../utilities/riskMatrix';
import InspectionItemInput from './InspectionItemInput';
import {
  statusType,
  inputTypes,
  getExtension,
  evaluateCondition,
} from '../../../utilities/inspection';
import findFileIcon from '../../../utilities/files';
import { useInspection } from '../../../hooks/offline-hooks/createInspectionhook';
import ImageWrapper from '../../offline-wrapper-functions/image-wrapper';
import { useTranslation } from 'react-i18next';
import {
  keys,
  getTranslationKey,
} from '../../../utilities/translator/translation_keys.js';
import { getLocalTime } from '../../../utilities/time.js';
import { downloadFile } from '../../../utilities/inspection';
import AudioWrapper from '../../offline-wrapper-functions/audio-wrapper.js';
import { numberCheck } from '../../../utilities/inspection';
import { useModal } from '../../../providers/ModalProvider.jsx';
import {
  openFailureModal,
  openFullPageImageModal,
} from '../../../graphql/cache/modal.js';
import { customColors } from '../../../utilities/misc.js';
import InspectionItemComments from './InspectionItemComments.jsx';
import { useOnlineStatus } from '../../../hooks/offline-hooks/offline-misc.js';
import InspectionItemAsset from './InspectionItemAsset.jsx';
import { modals } from '../../../providers/modals.js';
import { useActionItemModal } from '../../../hooks/misc.js';
import TextWithIcon from '../../typography/TextWithIcon.jsx';
import { paths } from '../../../constants/strings.js';
import { useNavigate } from 'react-router-dom';
import { actionsKeys } from '../../question_menu/conditionalActions/conditionalAction.utils.js';
import RichTextEditor from '../../rich_text/RichTextEditor.jsx';
import Spacer from '../../typography/Spacer.jsx';
import classNames from 'classnames';
import RiskRegistrySelector from '../../../pages/risk_registry/RiskRegistrySelector.jsx';

const addActionItemMutation = loader('./InspectionItemText.actionItem.graphql');
const deleteActionItemMutation = loader(
  './InspectionItemText.actionItemDelete.graphql',
);

const getStatusIcon = (item) => {
  switch (item.status) {
    case statusType.pass:
      return 'check_circle';
    case statusType.notApplicable:
      return 'check_box';
    default:
      return 'cancel';
  }
};

export default function InspectionItemText({
  title,
  description,
  inputType,
  questionId,
  inspectionId,
  inspectionTitle,
  inspectionType,
  vehicleUnitNumber,
  additionalData,
  item,
  required,
  workspaceId,
  internalType,
  refetch,
  isBundle,
  inspectionVehicleId,
  question,
  refetchCPA = () => {},
  refetchActionItems = () => {},
  cpas = [],
  parentItem,
  onEdit = () => {},
  showAllComments,
  onCommentIconClick = () => {},
  readOnly,
  inspection = {},
  preview = false,
  prefillCandidates,
}) {
  const user = item?.user;
  const fetchedActionItems = useMemo(
    () => inspection?.actionItems ?? [],
    [inspection],
  );
  const [isEditing, setIsEditing] = useState(!item?.value);
  const isAddingRef = useRef(false);
  const { addInspectionTestResults, updateInspectionTestResults } =
    useInspection();
  const [addActionItem] = useMutation(addActionItemMutation);
  const [deleteActionItem] = useMutation(deleteActionItemMutation);
  const [isRecording, setIsRecording] = useState(false);
  const [expandTranscript, setExpandTranscript] = useState(false);
  const [editingComponent, setEditingComponent] = useState(null);
  const [showComments, setShowComments] = useState(false);
  const [editValue, setEditValue] = useState(item?.value || '');
  const { openConfirmationModal } = useModal();
  const isOnline = useOnlineStatus();
  const [fullScreen, setFullScreen] = useState(false);
  const { openModal } = useModal();
  const { openActionItemModal } = useActionItemModal();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const audioRecordingName = `${t(
    keys.assessments.VOICE_RECORDING,
  )} ${getLocalTime(item?.dateModified).format('YYYY-MM-DD')}`;

  const textDisplay = [
    inputTypes.time,
    inputTypes.dateTime,
    inputTypes.text,
    inputTypes.multiText,
    inputTypes.number,
    inputTypes.gps,
    inputTypes.dropdownSingle,
  ];

  const editableInputs = [
    ...textDisplay,
    inputTypes.date,
    inputTypes.image,
    inputTypes.signature,
    inputTypes.file,
    inputTypes.test,
    inputTypes.user,
    inputTypes.audio,
    inputTypes.asset,
  ];

  const nonEditableInputs = [
    inputTypes.multiSignature,
    inputTypes.multiFile,
    inputTypes.multiImage,
    inputTypes.multiUser,
    inputTypes.matrix,
    inputTypes.multiSelect,
    inputTypes.select,
    inputTypes.table,
    inputTypes.dropdownMulti,
    inputTypes.selectChecklist,
    inputTypes.riskRegistry,
  ];

  useEffect(() => {
    if (!item?.value && !isAddingRef.current) {
      setIsEditing(true);
      setEditValue('');
    } else if (inputType === inputTypes.table && item) {
      // Table handles dirty-cell merging internally; always forward fresh poll data
      setEditValue(item.value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item]);

  const isAudit = inspectionType === 'AUDIT';
  const itemCorrectiveActions = useMemo(
    () => cpas?.filter((c) => c?.question?.id === item?.questionId) ?? [],
    [cpas, item?.questionId],
  );

  const vehicle = item?.vehicle;

  const { assignees, actionItems } = useMemo(() => {
    if (isAudit) {
      return {
        assignees: Array.from(
          new Map(
            itemCorrectiveActions
              .filter((c) => c?.assignee)
              .map((c) => [c.assignee.id, c.assignee]),
          ).values(),
        ).sort((a, b) => a.firstName?.localeCompare(b.firstName)),
        actionItems: [],
      };
    }

    const matchesThisItem = (ai) => ai.inspectionItemId === item.id;

    if (!fetchedActionItems?.length || !item) {
      return { assignees: [], actionItems: [] };
    }

    return {
      assignees: Array.from(
        new Map(
          fetchedActionItems
            .filter(matchesThisItem)
            .filter((ai) => ai.assignee)
            .map((ai) => [ai.assignee.id, ai.assignee]),
        ).values(),
      ).sort((a, b) => a.firstName?.localeCompare(b.firstName)),
      actionItems: fetchedActionItems
        .filter(matchesThisItem)
        .sort((a, b) => a.title?.localeCompare(b.title)),
    };
  }, [fetchedActionItems, item, isAudit, itemCorrectiveActions]);

  const handleFail = ({
    failNotes,
    userId,
    itemId,
    value,
    failed,
    vehicleId,
    failImage,
    probability,
    impact,
    additional,
    status,
  }) => {
    const actionItemTitle =
      inspectionType === 'VEHICLE'
        ? t(keys.assessments.VEHICLE_FAIL, {
            title: title,
            unit: vehicleUnitNumber,
          })
        : t(keys.assessments.REPORT_FAIL, {
            question: title,
            template: inspectionTitle,
          });

    const actionItemDescription = `${failNotes}`;

    const prevStatus = item?.status;
    const newStatus = status;

    if (userId && !isAudit && status === statusType.fail) {
      addActionItem({
        variables: {
          title: actionItemTitle,
          description: actionItemDescription,
          assigneeId: userId,
          type: inspectionType,
          inspectionId: inspectionId,
          vehicleId: inspectionVehicleId,
          workspaceId: workspaceId,
          questionId,
          inspectionItemId: itemId,
        },
      }).then(
        ({
          data: {
            addActionItem: { id: actionItemId },
          },
        }) => {
          if (!itemId) {
            addInspectionTestResults({
              inspectionId,
              questionId: questionId,
              value,
              failed,
              status,
              failNotes,
              failImage,
              userId: userId || null,
              additional,
              impact,
              probability,
              inputType,
              vehicleId,
              parentBundleId: parentItem?.bundleId ?? null,
            });
          } else {
            updateInspectionTestResults({
              inspectionId,
              itemId,
              value,
              failed,
              status,
              failNotes,
              failImage,
              userId: userId || null,
              additional,
              impact,
              probability,
              questionId,
              inputType,
              vehicleId,
            });
          }
          refetchActionItems();
        },
      );
    } else if (
      prevStatus === statusType.fail &&
      newStatus !== statusType.fail
    ) {
      if (actionItems.length > 0) {
        openConfirmationModal({
          variant: 'warning',
          title: t(keys.action.DELETE_VARIABLE, {
            variable: t(keys.common.ACTION_ITEM, {
              count: actionItems.length,
            }),
          }),
          description: t(keys.action.DELETE_CONFIRMATION, {
            variable:
              actionItems.length === 1
                ? `${t(keys.common.ACTION_ITEM)} - ${actionItems[0]?.title}`
                : `${actionItems.length} ${t(keys.common.ACTION_ITEM, {
                    count: actionItems.length,
                  })}`,
          }),
          onSubmit: () =>
            Promise.all(
              actionItems.map((actionItem) =>
                deleteActionItem({
                  variables: { actionItemId: actionItem?.id },
                }),
              ),
            ).then(() => refetchActionItems()),
        });
      }
    }
  };

  const onSubmit = (params) => {
    const {
      value,
      failed = item?.failed ?? false,
      status = item?.status ?? statusType.pass,
      failNotes,
      failImage,
      userId,
      additional,
      impact,
      probability,
      vehicleId,
    } = params;
    if (item?.id) {
      const prevStatus = item?.status;
      const newStatus = status;
      const unfailing =
        prevStatus === statusType.fail && newStatus !== statusType.fail;
      updateInspectionTestResults({
        inspectionId,
        itemId: item.id,
        value,
        failed,
        status,
        failNotes,
        failImage: unfailing ? null : failImage,
        userId: userId || null,
        additional,
        impact,
        probability,
        questionId,
        inputType,
        vehicleId,
      }).then(() =>
        handleFail({
          failNotes,
          userId: userId || null,
          itemId: item?.id,
          value,
          failed,
          vehicleId,
          failImage,
          probability,
          impact,
          additional,
          status,
        }),
      );
      setIsEditing(false);
    } else {
      if (isAddingRef.current) return;
      isAddingRef.current = true;
      addInspectionTestResults({
        inspectionId,
        questionId,
        value,
        failed,
        status,
        failNotes,
        failImage,
        userId: userId || null,
        additional,
        impact,
        probability,
        inputType,
        vehicleId,
        parentBundleId: parentItem?.bundleId ?? null,
      })
        .then((res) => {
          isAddingRef.current = false;
          const newItemId = res?.data?.addInspectionItem?.id;
          handleFail({
            failNotes,
            userId: userId || null,
            itemId: newItemId,
            value,
            failed,
            vehicleId,
            failImage,
            probability,
            impact,
            additional,
            status,
          });
        })
        .catch(() => {
          isAddingRef.current = false;
          refetch?.();
        });
      setIsEditing(false);
    }
    if (Array.isArray(question?.conditionalAction)) {
      question.conditionalAction
        .filter((ca) =>
          evaluateCondition(value, {
            inputType: question?.inputType,
            conditionalAction: ca,
          }),
        )
        .flatMap((ca) => ca.actions ?? [])
        .forEach(({ action, additionalData }) => {
          if (action === actionsKeys.popUp) {
            openConfirmationModal({
              title: additionalData?.title || '',
              description: additionalData?.message || '',
              variant: 'warning',
              hideSubmit: true,
              cancelText: t(keys.common.CLOSE),
              files: additionalData?.files || [],
            });
          }
        });
    }
  };
  useEffect(() => {
    if (!!item?.required) {
      onEdit(isEditing);
    }
  }, [isEditing, item?.required, onEdit]);

  return (
    <div className={styles.container}>
      <div className={styles.leftContainer}>
        <div className={styles.header}>
          {inputType === 'MESSAGE' ? (
            <RichTextEditor
              readOnly
              value={title}
              className={styles.richTextContainer}
            />
          ) : (
            <div className={styles.subHeader}>
              {inputType !== 'MESSAGE' && (
                <Text weight="bold" noMargin>
                  {`${
                    !!internalType
                      ? t(getTranslationKey(title, 'assessments'))
                      : title
                  }`}{' '}
                  <span style={{ color: 'red', fontWeight: 'bold' }}>
                    {' '}
                    {`${required ? '*' : ''}`}
                  </span>
                </Text>
              )}
            </div>
          )}
          {isEditing || (item?.failed && inspection?.isDraft) ? (
            <div className={styles.rightIcons}>
              {!!item?.value && (
                <div className={styles.rightIcons}>
                  <Icon
                    className={styles.commentIcon}
                    sx={{
                      fontSize: '1.3rem',
                      opacity: 0.5,
                      cursor: 'pointer',
                    }}
                    onClick={() => setIsEditing(false)}
                  >
                    undo
                  </Icon>
                  <Icon
                    sx={{
                      cursor: 'pointer',
                      fontSize: '1.3rem',
                      color: customColors.RED_LIGHT,
                    }}
                    onClick={() => {
                      openConfirmationModal({
                        variant: 'warning',
                        title: 'Clear Answer',
                        description:
                          'Are you sure you want to clear this answer? This action cannot be undone.',
                        onSubmit: () => {
                          onSubmit({
                            value: '',
                            failed: false,
                            failNotes: null,
                            failImage: null,
                            status: statusType.pass,
                          });
                          setIsEditing(false);
                        },
                      });
                    }}
                  >
                    cancel
                  </Icon>
                </div>
              )}
            </div>
          ) : (
            <div
              className={styles.rightIcons}
              style={inputType === 'MESSAGE' ? {} : { width: '100%' }}
            >
              {!!item?.value && (
                <Icon
                  baseClassName="material-icons-outlined"
                  className={styles[`${item.status.toLowerCase()}Icon`]}
                  sx={{ fontSize: '1.2rem' }}
                >
                  {getStatusIcon(item)}
                </Icon>
              )}
              {inputType !== inputTypes.test &&
                inputType !== inputTypes.user &&
                !item?.failed &&
                !!item?.value &&
                !readOnly &&
                !isEditing && (
                  <Icon
                    sx={{
                      color: customColors.RED_LIGHT,
                      fontSize: '1.2rem',
                    }}
                    className={styles.icon}
                    onClick={() => {
                      isAudit
                        ? openModal({
                            modalName: modals.CPA,
                            variables: {
                              title,
                              questionId,
                              inspectionId,
                              onSubmit: ({ failNotes, userId }) => {
                                onSubmit({
                                  value: item?.value,
                                  failed: true,
                                  status: statusType.fail,
                                  failNotes,
                                  userId,
                                });
                                refetchCPA();
                              },
                            },
                          })
                        : openFailureModal({
                            questionId,
                            title,
                            description,
                            onSubmit: (notes, imageUrl, assigneeId) => {
                              onSubmit({
                                value: item?.value,
                                failNotes: notes,
                                failImage: imageUrl,
                                userId: assigneeId,
                                failed: true,
                                status: statusType.fail,
                                additional: item?.additional,
                                impact: item?.impact,
                                probability: item?.probability,
                              });
                            },
                          });
                    }}
                  >
                    flag
                  </Icon>
                )}
              {editableInputs?.includes(inputType) && !readOnly && (
                <Icon
                  baseClassName="material-icons-outlined"
                  color="disabled"
                  onClick={() => {
                    if (inputType === inputTypes.audio) {
                      openConfirmationModal({
                        title: t(keys.assessments.CLEAR_RECORDING),
                        variant: 'danger',
                        buttonText: t(keys.action.CLEAR),
                        description: t(
                          keys.assessments.CLEAR_RECORDING_WARNING,
                        ),
                        onSubmit: () => {
                          updateInspectionTestResults({
                            inspectionId: inspectionId,
                            itemId: item.id,
                            value: '',
                            failed: false,
                            status: 'PASS',
                          });
                          setIsEditing(!isEditing);
                          setEditValue('');
                        },
                      });
                    } else {
                      setIsEditing(!isEditing);
                      setEditValue(item?.value ?? '');
                      setEditingComponent(item?.id);
                    }
                  }}
                  className={styles.icon}
                  sx={{ fontSize: '1.3rem' }}
                >
                  create
                </Icon>
              )}
            </div>
          )}
          {isOnline && (
            <Icon
              className={styles.commentIcon}
              color={
                showAllComments || showComments || item?.comments?.length
                  ? 'primary'
                  : 'disabled'
              }
              baseClassName="material-icons-outlined"
              sx={{ fontSize: '1.3rem' }}
              onClick={() => {
                if (!preview) {
                  if (!item?.id) {
                    onSubmit({ value: '' });
                  }
                  setShowComments(!showComments);
                  onCommentIconClick();
                }
              }}
            >
              {showComments || showAllComments
                ? 'speaker_notes_off'
                : item?.comments?.length
                ? 'mark_unread_chat_alt'
                : 'chat'}
            </Icon>
          )}
        </div>
        {!!description && (
          <>
            <Text noMargin size="sm">
              {description}
            </Text>
          </>
        )}
        <Spacer size="xxs" />
        {inputType === inputTypes.number && !!additionalData ? (
          <Text
            className={styles.numberText}
            size="sm"
            color="secondary"
            noMargin
          >
            {numberCheck(additionalData)}
          </Text>
        ) : null}
        {user && !isEditing && inputType === inputTypes.user && (
          <SimpleUserCard
            size="md"
            weight="semiBold"
            largeAvatar
            className={styles.userCard}
            user={user}
          />
        )}
        {(isEditing || nonEditableInputs?.includes(inputType)) && !readOnly && (
          <div className={styles.inputQuestions}>
            <InspectionItemInput
              preview={preview}
              workspaceId={workspaceId}
              assetId={inspectionVehicleId}
              setIsEditing={setIsEditing}
              refetchCPA={refetchCPA}
              item={item}
              inputType={inputType}
              title={title}
              question={question}
              questionId={questionId}
              inspectionId={inspectionId}
              prefillCandidates={prefillCandidates}
              editingComponent={editingComponent}
              inspectionType={inspectionType}
              additionalData={
                inputType === inputTypes.multiSignature ||
                inputType === inputTypes.multiFile
                  ? item?.additional
                  : additionalData
              }
              isEditing={isEditing}
              inputValue={editValue}
              setInputValue={setEditValue}
              isDraft={!readOnly}
              isRecording={isRecording}
              setIsRecording={setIsRecording}
              isBundle={isBundle}
              onSubmit={(
                value,
                failed = false,
                status = statusType.pass,
                failNotes,
                failImage,
                userId,
                additional,
                impact,
                probability,
              ) => {
                !preview &&
                  onSubmit(
                    value,
                    failed,
                    status,
                    failNotes,
                    failImage,
                    userId,
                    additional,
                    impact,
                    probability,
                  );
              }}
            />
          </div>
        )}
        {inputType === inputTypes.asset && (!isEditing || readOnly) ? (
          <InspectionItemAsset vehicle={vehicle} readOnly={readOnly} />
        ) : null}
        {readOnly && !item?.value && inputType !== 'MESSAGE' && (
          <Text className={styles.textResponse} color="primary" noMargin>
            {t(keys.assessments.NO_RESPONSE)}
          </Text>
        )}
        {inputType === inputTypes.audio && !isEditing && item?.additional && (
          <div>
            <div className={styles.audioContainer}>
              <div className={styles.audioCard}>
                <img
                  className={styles.fileIcon}
                  alt="audio recording"
                  src={findFileIcon('audio')}
                />
                <Text noMargin weight="semiBold">
                  {audioRecordingName}
                </Text>
                <AudioWrapper audio={item?.additional}>
                  {t(keys.assessments.UNSUPPORTED_AUDIO)}
                </AudioWrapper>
                <Icon
                  baseClassName="material-icons-outlined"
                  className={styles.icon}
                  color="primary"
                  onClick={() =>
                    downloadFile(item?.additional, audioRecordingName)
                  }
                >
                  download
                </Icon>
              </div>
            </div>
            <Text
              color="secondary"
              size="sm"
              hover
              className={styles.viewTranscript}
              textAlign="center"
              onClick={() => setExpandTranscript(!expandTranscript)}
            >
              {expandTranscript
                ? t(keys.assessments.HIDE_TRANSCRIPT)
                : t(keys.assessments.VIEW_TRANSCRIPT)}
            </Text>
            <Text
              color="primary"
              noMargin
              className={expandTranscript ? styles.textOverflow : styles.hide}
            >
              {item?.value || t(keys.common.NONE)}
            </Text>
          </div>
        )}
        {inputType === inputTypes.multiFile && readOnly && !!item?.value && (
          <div className={styles.multiFileDisplay}>
            {item?.additional?.split('|').map((additional, i) => (
              <div
                key={`$file-${additional}-${i}`}
                className={styles.fileDisplay}
                onClick={(e) => {
                  internalType === 'RECORDED_MEETING'
                    ? e.stopPropagation()
                    : openConfirmationModal({
                        title: t(keys.action.DOWNLOAD_VARIABLE, {
                          variable: additional,
                        }),
                        description: t(keys.action.DOWNLOAD_CONFIRMATION, {
                          variable: additional,
                        }),
                        variant: 'warning',
                        onSubmit: () => {
                          const url = item?.value?.split('|')[i];
                          downloadFile(url, additional);
                        },
                      });
                }}
              >
                <div className={styles.card}>
                  <img
                    className={styles.fileIcon}
                    alt={`${additional} file`}
                    src={findFileIcon(
                      getExtension(
                        item?.value?.includes('|')
                          ? item?.value.split('|')[i]
                          : item?.value,
                      ),
                    )}
                  />
                  <Text noMargin weight="semiBold">
                    {additional}
                  </Text>
                </div>
                {internalType === 'RECORDED_MEETING' ? (
                  <div>
                    <AudioWrapper audio={item?.value?.split('|')[i]}>
                      {t(keys.assessments.UNSUPPORTED_AUDIO)}
                    </AudioWrapper>
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        )}
        {inputType === inputTypes.file && !isEditing && (
          <div
            className={styles.fileDisplay}
            onClick={() => {
              openConfirmationModal({
                title: t(keys.action.DOWNLOAD_VARIABLE, {
                  variable: item?.additional,
                }),
                description: t(keys.action.DOWNLOAD_CONFIRMATION, {
                  variable: item?.additional,
                }),
                variant: 'warning',
                onSubmit: () => {
                  downloadFile(item?.value, item?.additional);
                },
              });
            }}
          >
            <div className={styles.card}>
              <img
                className={styles.fileIcon}
                alt={`${item?.additional} file`}
                src={findFileIcon(getExtension(item?.value ? item?.value : ''))}
              />
              <Text noMargin weight="semiBold">
                {item?.additional}
              </Text>
            </div>
          </div>
        )}
        {inputType === inputTypes.multiSignature && readOnly && (
          <div className={styles.multiSignatureDisplay}>
            {item?.value?.split('|').map((url, i) => (
              <div key={`${item?.id} + ${i}`}>
                <Text color="primary" noMargin className={styles.textResponse}>
                  {item?.additional?.split('|')[i]}
                </Text>
                <ImageWrapper
                  key={url}
                  alt="signature2"
                  className={styles.displayImage}
                  image={url}
                />
              </div>
            ))}
          </div>
        )}
        {inputType === inputTypes.multiUser && readOnly && (
          <div className={styles.usersContainer}>
            {item?.users?.map((user, i) => (
              <div key={`${user.id} + ${i}`}>
                <SimpleUserCard size="md" mediumAvatar user={user} />
              </div>
            ))}
          </div>
        )}
        {inputType === inputTypes.riskRegistry && readOnly && (
          <RiskRegistrySelector
            hideTitle
            readOnly
            modelType={'inspection'}
            modelId={parseInt(inspectionId)}
          />
        )}
        {inputType === inputTypes.test &&
          (readOnly || !isEditing) &&
          item?.value && (
            <Text
              noMargin
              weight="semiBold"
              color={item?.failed ? 'red' : 'green'}
            >
              {(() => {
                const options = additionalData?.split('|');
                if (item.value === 'PASS')
                  return (
                    options?.[0] ||
                    t(getTranslationKey('PASS', 'common')) ||
                    item.value
                  );
                if (item.value === 'FAIL')
                  return (
                    options?.[1] ||
                    t(getTranslationKey('FAIL', 'common')) ||
                    item.value
                  );
                if (item.value === 'NOT_APPLICABLE')
                  return options?.[2] || 'N/A';
                return t(getTranslationKey(item.value, 'common')) || item.value;
              })()}
            </Text>
          )}
        {inputType === inputTypes.matrix && readOnly && item?.value && (
          <div className={styles.matrix}>
            <div
              className={styles.matrixLabel}
              style={{
                backgroundColor:
                  colorLookup[
                    calculateRisk(item?.impact * item?.probability).level
                  ],
              }}
            >
              <Text
                color="white"
                noMargin
                noSelect
                weight="semiBold"
                textAlign="center"
              >
                {calculateRisk(item?.impact * item?.probability).level}
              </Text>
            </div>

            <div className={styles.matrixText}>
              <Text color="primary" className={styles.textResponse} noMargin>
                <span
                  style={{
                    color: 'black',
                    fontFamily: 'eina03regular',
                    fontSize: 'small',
                  }}
                >
                  {`${t(keys.common.PROBABILITY)}: `}
                </span>
                {item?.probability}
              </Text>
              <Text color="primary" className={styles.textResponse} noMargin>
                <span
                  style={{
                    color: 'black',
                    fontFamily: 'eina03regular',
                    fontSize: 'small',
                  }}
                >
                  {`${t(keys.common.IMPACT)}: `}
                </span>
                {item?.impact}
              </Text>
              <Text color="primary" className={styles.textResponse} noMargin>
                <span
                  style={{
                    color: 'black',
                    fontFamily: 'eina03regular',
                    fontSize: 'small',
                  }}
                >
                  Risk Score:{' '}
                </span>
                {item?.impact * item?.probability}
              </Text>
            </div>
          </div>
        )}
        {inputType === inputTypes.signature && !isEditing && (
          <div className={styles.sigSingle}>
            <Text color="primary" className={styles.textResponse} noMargin>
              {item?.additional}
            </Text>
            <ImageWrapper
              alt="signature3"
              className={styles.displayImage}
              image={item?.value}
            />
          </div>
        )}
        {inputType === inputTypes.image && !isEditing && (
          <div>
            <ImageWrapper
              alt="uploaded"
              className={classNames(styles.image, styles.hover)}
              image={item?.value}
              onClick={(e) =>
                openFullPageImageModal({
                  image: {
                    imageUrl: e.target.src,
                    description: t(keys.common.IMAGE),
                  },
                })
              }
            />
            {!readOnly && (
              <div className={styles.deleteIconContainer}>
                <Icon
                  className={styles.imgDelete}
                  baseClassName="material-icons-outlined"
                  onClick={() =>
                    openConfirmationModal({
                      title: t(keys.action.DELETE_VARIABLE, {
                        variable: t(keys.common.IMAGE),
                      }),
                      description: t(keys.action.DELETE_CONFIRMATION, {
                        variable: t(keys.common.IMAGE),
                      }),
                      variant: 'warning',
                      onSubmit: () =>
                        updateInspectionTestResults({
                          inspectionId: inspectionId,
                          itemId: item.id,
                          value: '',
                          failed: false,
                          status: 'PASS',
                        }),
                    })
                  }
                >
                  delete
                </Icon>
              </div>
            )}
          </div>
        )}
        {inputType === inputTypes.multiImage &&
          readOnly &&
          (item?.value ? (
            <div className={styles.multiImage}>
              {item?.value?.split('|').map((img, i) => (
                <div key={`${item?.id}-${i}`}>
                  <ImageWrapper
                    alt="uploaded"
                    className={classNames(styles.image, styles.hover)}
                    image={img}
                    onClick={(e) =>
                      openFullPageImageModal({
                        image: {
                          imageUrl: e.target.src,
                          description: t(keys.common.IMAGE),
                        },
                      })
                    }
                  />
                  {!readOnly && (
                    <div className={styles.deleteIconContainer}>
                      <Icon
                        className={styles.imgDelete}
                        baseClassName="material-icons-outlined"
                        onClick={() =>
                          openConfirmationModal({
                            title: t(keys.action.DELETE_VARIABLE, {
                              variable: t(keys.common.IMAGE),
                            }),
                            description: t(keys.action.DELETE_CONFIRMATION, {
                              variable: t(keys.common.IMAGE),
                            }),
                            variant: 'warning',
                            onSubmit: () => {
                              const images = item.value?.split('|');
                              const newImages = images.filter(
                                (_, idx) => idx !== i,
                              );
                              const newString = newImages.join('|');
                              updateInspectionTestResults({
                                inspectionId: inspectionId,
                                itemId: item.id,
                                value: newString,
                                failed: false,
                                status: 'PASS',
                              });
                            },
                          })
                        }
                      >
                        delete
                      </Icon>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : null)}
        {(textDisplay?.includes(inputType) && !isEditing) ||
        (inputType === inputTypes.select && readOnly) ||
        (inputType === inputTypes.date && !isEditing) ? (
          <Text
            noMargin
            color="primary"
            prewrap
            className={styles.textResponse}
          >
            {item?.value}
          </Text>
        ) : null}
        {inputType === inputTypes.table && readOnly && item?.value && (
          <div
            className={fullScreen ? styles.fullScreen : styles.tableContainer}
          >
            {fullScreen && (
              <div className={styles.header}>
                <Text size="lg" weight="bold">
                  {title}
                </Text>
                <Icon
                  className={styles.hover}
                  onClick={() => setFullScreen(false)}
                >
                  close
                </Icon>
              </div>
            )}
            <div className={styles.tableContainer}>
              <Table striped bordered className={styles.table}>
                <thead>
                  <tr>
                    {additionalData?.split('|').map((col, i) => (
                      <th
                        className={styles.tableHeader}
                        key={`table-${col}-${i}`}
                        style={{ minWidth: '10rem' }}
                      >
                        <Text weight="semiBold" noMargin size="sm">
                          {col}
                        </Text>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {item?.value?.split('|').map((data, rowIndex) => (
                    <tr key={`top-cell-${rowIndex}`}>
                      {data.split('\n').map((item, colIndex) => (
                        <td key={`cell-${rowIndex}-${colIndex}`}>
                          <Text noMargin size="sm">
                            {item}
                          </Text>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
            {!fullScreen && (
              <Text
                hover
                onClick={() => setFullScreen(true)}
                noMargin
                size="sm"
                color="accentPrimary"
                textAlign="center"
              >
                VIEW FULLSCREEN
              </Text>
            )}
          </div>
        )}
        {(inputType === inputTypes.multiSelect ||
          inputType === inputTypes.dropdownMulti) &&
          readOnly && (
            <Text color="primary" className={styles.textResponse} noMargin>
              {item?.value?.replace(/\|/g, ', ')}
            </Text>
          )}
        {inputType === inputTypes.selectChecklist && readOnly && (
          <div>
            {additionalData?.split('|').map((d) => {
              const selected = item?.value?.split('|');
              return (
                <TextWithIcon
                  key={d}
                  leftIcon={selected?.includes(d) ? 'check' : 'close'}
                  iconProps={{
                    fontSize: '1rem',
                    color: selected?.includes(d) ? 'green' : 'red',
                  }}
                  color="primary"
                  className={styles.textResponse}
                  noMargin
                >
                  {d}
                </TextWithIcon>
              );
            })}
          </div>
        )}
        {!!item?.failed && item?.status === 'FAIL'
          ? !!item?.failNotes &&
            !isEditing && (
              <div className={styles.failCard}>
                <div className={styles.failContainer}>
                  <div>
                    <div className={styles.actionItemLinkList}>
                      {isAudit &&
                        itemCorrectiveActions.map((cpa) => (
                          <TextWithIcon
                            key={cpa.id}
                            size="sm"
                            noMargin
                            link
                            weight="semiBold"
                            hover
                            textAlign="right"
                            color="accentPrimary"
                            onClick={() => navigate(`/${paths.cpa}/${cpa.id}`)}
                          >
                            {cpa.externalId}
                          </TextWithIcon>
                        ))}
                    </div>
                    <div className={styles.failComponentTop}>
                      <Text weight="semiBold" color="red" noMargin size="sm">
                        {t(
                          isAudit
                            ? keys.action_items.ACTION_ASSIGNED
                            : keys.action_items.ASSIGNED_TO,
                        )}
                      </Text>
                      <div className={styles.assigneeList}>
                        {assignees?.length ? (
                          assignees.map((assignee) => (
                            <SimpleUserCard
                              size="md"
                              weight="semiBold"
                              user={assignee}
                            />
                          ))
                        ) : (
                          <Text size="sm" color="secondary" noMargin>
                            {t(keys.common.NONE)}
                          </Text>
                        )}
                      </div>
                      {!isAudit && !readOnly && (
                        <TextWithIcon
                          size="sm"
                          noMargin
                          weight="semiBold"
                          hover
                          icon={'add'}
                          textAlign="right"
                          color="accentPrimary"
                          onClick={() =>
                            addActionItem({
                              variables: {
                                title:
                                  inspectionType === 'VEHICLE'
                                    ? t(keys.assessments.VEHICLE_FAIL, {
                                        title: title,
                                        unit: vehicleUnitNumber,
                                      })
                                    : t(keys.assessments.REPORT_FAIL, {
                                        question: title,
                                        template: inspectionTitle,
                                      }),
                                type: inspectionType,
                                inspectionId: inspectionId,
                                vehicleId: inspectionVehicleId,
                                workspaceId: workspaceId,
                                questionId,
                                inspectionItemId: item?.id,
                              },
                            }).then(
                              ({
                                data: {
                                  addActionItem: { id: actionItemId },
                                },
                              }) =>
                                actionItemId &&
                                openActionItemModal(
                                  actionItemId,
                                  inspectionType,
                                  refetchActionItems,
                                ),
                            )
                          }
                        >
                          {t(keys.common.ACTION_ITEM)}
                        </TextWithIcon>
                      )}
                    </div>
                    <div className={styles.failComponent}>
                      <Text
                        weight="semiBold"
                        color="red"
                        size="sm"
                        noMargin
                        className={styles.left}
                      >
                        {t(keys.assessments.REASON_FOR_FAILURE)}
                      </Text>
                      <Text size="md" weight="semiBold" prewrap noMargin>
                        {item.failNotes || 'None'}
                      </Text>
                    </div>
                    {actionItems?.length && !isAudit && isOnline ? (
                      <div
                        className={classNames(
                          styles.failComponent,
                          styles.failImageComponent,
                        )}
                      >
                        <Text
                          weight="semiBold"
                          color="red"
                          size="sm"
                          noMargin
                          className={styles.left}
                        >
                          {t(keys.common.ACTION_ITEMS)}
                        </Text>
                        <div>
                          {actionItems.map((ai) => (
                            <div className={styles.flexRow}>
                              <TextWithIcon
                                size="sm"
                                noMargin
                                link
                                weight="semiBold"
                                hover
                                color="accentPrimary"
                                onClick={() =>
                                  openActionItemModal(
                                    ai?.id,
                                    inspectionType,
                                    refetchActionItems,
                                  )
                                }
                              >
                                {ai?.title}
                              </TextWithIcon>
                              {!ai?.assignee && (
                                <Tooltip
                                  title={'This action item is unassigned'}
                                >
                                  <Icon
                                    style={{
                                      fontSize: '1rem',
                                      color: customColors.ORANGE,
                                      alignSelf: 'center',
                                    }}
                                  >
                                    error_outlined
                                  </Icon>
                                </Tooltip>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <></>
                    )}

                    {item?.failImage && (
                      <div
                        className={classNames(
                          styles.failComponent,
                          styles.failImageComponent,
                        )}
                      >
                        <Text
                          weight="semiBold"
                          color="red"
                          noMargin
                          className={styles.left}
                        >
                          {`${t(keys.assessments.FAIL_IMAGE)}`}
                        </Text>
                        <div className={styles.multiImage}>
                          {item.failImage.split('|').map((img, index) => (
                            <ImageWrapper
                              key={`${item.id}-fail-image-${index}`}
                              image={img}
                              className={classNames(
                                styles.failImage,
                                styles.hover,
                              )}
                              alt="failure"
                              onClick={(e) =>
                                openFullPageImageModal({
                                  image: {
                                    imageUrl: e.target.src,
                                    description: t(keys.assessments.FAIL_IMAGE),
                                  },
                                })
                              }
                            />
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )
          : null}
        {showComments || showAllComments ? (
          <InspectionItemComments
            itemId={item?.id}
            comments={item?.comments || []}
            refetch={refetch}
          />
        ) : null}
      </div>
    </div>
  );
}
